import {
	CalendarDaysIcon,
	ClockIcon,
	ReceiptPercentIcon,
	Squares2X2Icon,
	TicketIcon
} from '@heroicons/react/24/outline'
import { Controller, useController, useWatch } from 'libs/form'
import type { FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { DatePicker, FormSection, Select, Switch } from 'shared/components'
import { useFetchInfinite } from 'shared/hooks'
import { InputGroup } from 'shared/molecules'

type CouponFormSectionsProps = {
	form: UseFormReturn<FieldValues>
}

export const CouponFormSections = ({ form }: CouponFormSectionsProps) => {
	const { t } = useTranslation()
	const {
		register,
		control,
		trigger,
		formState: { errors }
	} = form
	const fieldErrors = errors as any
	const boothType = useWatch({ control, name: 'booth_type' })
	const [bookingWindowEnd, validUntil] = useWatch({
		control,
		name: ['booking_window_end', 'valid_until']
	})
	const { field: rentalField } = useController({ control, name: 'rental' })

	const { data: boothTypes, fetchNextPage: fetchNextAreaTypes } = useFetchInfinite({
		name: 'fetchAreaTypes',
		endpoint: '/booth-types/'
	})
	// ponytail: keyed per booth type so it refetches; useFetchInfinite breaks page 2+ of a
	// filtered list (two `?`), fine while a booth type has one page of rentals
	const { data: rentals, fetchNextPage: fetchNextRentals } = useFetchInfinite({
		name: `fetchCouponRentals-${boothType}`,
		endpoint: '/rentals/',
		query: `booth_type=${boothType}`,
		enabled: boothType != null
	})

	const boothTypeOptions = (boothTypes?.pages ?? [])
		.flatMap((page: any) => page.results)
		.map((item: any) => ({ label: item.name, value: item.id }))
	const rentalOptions = (rentals?.pages ?? [])
		.flatMap((page: any) => page.results)
		.map((item: any) => ({
			label: item.title || t('{{ count }} days', { count: item.duration }),
			value: item.id
		}))

	const renderDatePicker = (name: string, label: string, onPicked?: () => void) => (
		<Controller
			control={control}
			name={name}
			render={({ field }) => (
				<DatePicker
					label={label}
					selected={field.value}
					required
					error={fieldErrors[name]}
					onChange={(date: Date | null) => {
						field.onChange(date)
						onPicked?.()
					}}
				/>
			)}
		/>
	)

	return (
		<>
			<FormSection
				title={t('Code')}
				description={t('What customers type in when they book.')}
				icon={<TicketIcon />}>
				<InputGroup
					id="code"
					label={t('Code')}
					placeholder="SUMMER10"
					required
					{...register('code')}
					error={fieldErrors.code}
				/>
				<InputGroup
					id="max_uses"
					type="number"
					step="1"
					label={t('Max uses')}
					placeholder={t('Unlimited')}
					{...register('max_uses')}
					error={fieldErrors.max_uses}
				/>
			</FormSection>

			<FormSection
				title={t('Price discount')}
				description={t('How much is taken off the booking price.')}
				icon={<ReceiptPercentIcon />}
				tone="success">
				<InputGroup
					id="price_discount_value"
					type="number"
					step="0.01"
					className="sm:col-span-2"
					label={t('Value')}
					placeholder="0"
					required
					{...register('price_discount_value')}
					error={fieldErrors.price_discount_value}
				/>
				<Switch
					className="sm:col-span-2"
					label={t('Percentage')}
					description={t('The value is a percentage of the price, not a fixed amount.')}
					{...register('price_discount_is_percentage')}
				/>
			</FormSection>

			<FormSection
				title={t('Applies to')}
				description={t('Limit the code to one area type or rental. Leave empty for all.')}
				icon={<Squares2X2Icon />}
				tone="warning">
				<Controller
					control={control}
					name="booth_type"
					render={({ field }) => (
						<Select
							id="booth_type"
							variant="form"
							label={t('Area type')}
							placeholder={t('All')}
							options={boothTypeOptions}
							value={boothTypeOptions.find((option) => option.value === field.value) ?? null}
							handleInfiniteScroll={fetchNextAreaTypes}
							error={fieldErrors.booth_type}
							onChange={(option: any) => {
								field.onChange(option?.value ?? null)
								rentalField.onChange(null)
							}}
						/>
					)}
				/>
				<Select
					id="rental"
					variant="form"
					label={t('Rental')}
					placeholder={t('All')}
					options={rentalOptions}
					value={rentalOptions.find((option) => option.value === rentalField.value) ?? null}
					isDisabled={boothType == null}
					handleInfiniteScroll={fetchNextRentals}
					error={fieldErrors.rental}
					onChange={(option: any) => rentalField.onChange(option?.value ?? null)}
				/>
			</FormSection>

			<FormSection
				title={t('Booking window')}
				description={t('Which check-in dates it discounts')}
				icon={<CalendarDaysIcon />}>
				{renderDatePicker(
					'booking_window_start',
					t('Start date'),
					() => bookingWindowEnd && trigger('booking_window_end')
				)}
				{renderDatePicker('booking_window_end', t('End date'))}
			</FormSection>

			<FormSection
				title={t('Validity')}
				description={t('When the code can be entered')}
				icon={<ClockIcon />}
				tone="danger">
				{renderDatePicker(
					'valid_from',
					t('Valid from'),
					() => validUntil && trigger('valid_until')
				)}
				{renderDatePicker('valid_until', t('Valid until'))}
				<Switch
					className="sm:col-span-2"
					label={t('Active')}
					description={t('Inactive codes cannot be used, even inside their validity dates.')}
					{...register('is_active')}
				/>
			</FormSection>
		</>
	)
}
